import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

// Outline of metropolitan France projected from real lat/lon coordinates
// x = (lon + 5) / 13.5 * 100 ; y = (51.2 - lat) / 9 * 100
const FRANCE_POLYGON = [
  [54.6, 1.9],  // Dunkerque
  [50.7, 2.8],  // Calais
  [48.9, 5.2],  // Boulogne-sur-Mer
  [48.5, 11.1], // Baie de Somme
  [43.4, 15.5], // Dieppe
  [37.8, 19.0], // Le Havre
  [34.0, 20.5], // Côte de Nacre
  [25.0, 17.2], // Cherbourg
  [25.9, 28.3], // Mont Saint-Michel
  [22.0, 28.3], // Saint-Malo
  [11.1, 27.2], // Côte de granit rose
  [3.0, 31.1],  // Brest
  [2.2, 35.2],  // Pointe du Raz
  [5.9, 37.8],  // Pays bigouden
  [12.2, 38.9], // Lorient
  [20.7, 43.7], // Saint-Nazaire
  [21.1, 47.2], // Noirmoutier
  [28.5, 56.0], // La Rochelle
  [29.2, 62.6], // Estuaire de la Gironde
  [28.1, 72.8], // Bassin d'Arcachon
  [25.5, 85.8], // Biarritz
  [23.9, 87.1], // Hendaye
  [37.0, 93.3], // Pyrénées centrales
  [48.1, 96.7], // Andorre
  [60.5, 97.4], // Cerbère
  [59.6, 90.6], // Narbonne
  [64.4, 86.7], // Sète
  [71.1, 86.1], // Camargue
  [77.0, 87.8], // Marseille
  [81.0, 90.0], // Toulon
  [85.9, 88.1], // Saint-Tropez
  [90.9, 83.3], // Nice
  [92.6, 82.4], // Menton
  [88.9, 67.8], // Alpes frontière italienne
  [88.1, 59.4], // Mont Blanc
  [81.5, 55.0], // Genève
  [82.2, 53.3], // Lac Léman
  [88.9, 41.1], // Jura
  [93.3, 40.2], // Bâle
  [97.8, 24.8], // Alsace (Rhin)
  [95.6, 23.9], // Wissembourg
  [88.9, 22.2], // Sarre
  [83.0, 18.9], // Luxembourg
  [73.0, 11.7], // Ardennes
  [60.7, 4.4]   // Lille
];

// Main population centers (same projection) with relative weights,
// so the dot density mimics a real nationwide network
const CITIES = [
  { x: 54.4, y: 26.0, w: 22, spread: 5.5 }, // Paris
  { x: 72.9, y: 60.4, w: 8, spread: 4 },    // Lyon
  { x: 77.0, y: 85.5, w: 7, spread: 3.5 },  // Marseille
  { x: 60.7, y: 6.5, w: 6, spread: 3.5 },   // Lille
  { x: 47.7, y: 84.4, w: 5, spread: 3.5 },  // Toulouse
  { x: 32.7, y: 70.7, w: 5, spread: 3.5 },  // Bordeaux
  { x: 25.6, y: 44.2, w: 4, spread: 3 },    // Nantes
  { x: 89.5, y: 83.0, w: 4, spread: 2.5 },  // Nice
  { x: 93.5, y: 29.0, w: 3, spread: 2.5 },  // Strasbourg
  { x: 24.6, y: 34.3, w: 3, spread: 3 },    // Rennes
  { x: 65.8, y: 84.0, w: 3, spread: 2.5 },  // Montpellier
  { x: 79.0, y: 66.8, w: 2, spread: 2.5 },  // Grenoble
  { x: 74.4, y: 43.1, w: 2, spread: 2.5 },  // Dijon
  { x: 82.8, y: 27.9, w: 2, spread: 2.5 },  // Nancy / Metz
  { x: 59.9, y: 60.2, w: 2, spread: 2.5 },  // Clermont-Ferrand
  { x: 42.1, y: 42.3, w: 2, spread: 3 },    // Tours
  { x: 45.2, y: 19.6, w: 2, spread: 2.5 },  // Rouen
  { x: 66.9, y: 21.6, w: 2, spread: 2.5 },  // Reims
  { x: 34.3, y: 22.4, w: 2, spread: 2.5 },  // Caen
  { x: 46.4, y: 59.7, w: 2, spread: 3 },    // Limoges
  { x: 5.5, y: 31.5, w: 2, spread: 2 }      // Brest
];

// Ray-casting algorithm to determine if a point is inside France polygon
const isInsideFrance = (x, y) => {
  let inside = false;
  for (let i = 0, j = FRANCE_POLYGON.length - 1; i < FRANCE_POLYGON.length; j = i++) {
    const xi = FRANCE_POLYGON[i][0], yi = FRANCE_POLYGON[i][1];
    const xj = FRANCE_POLYGON[j][0], yj = FRANCE_POLYGON[j][1];

    const intersect = ((yi > y) !== (yj > y))
        && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

// Approximate gaussian via sum of uniforms
const gauss = () => (Math.random() + Math.random() + Math.random() - 1.5) / 1.5;

// Generate points inside France: a uniform rural layer plus city clusters
const generatePoints = (count) => {
  const totalWeight = CITIES.reduce((s, c) => s + c.w, 0);
  const points = [];
  let attempts = 0;
  while (points.length < count && attempts < 60000) {
    attempts++;
    let x, y;
    if (Math.random() < 0.55) {
      // Clustered around a population center
      let r = Math.random() * totalWeight;
      const city = CITIES.find(c => (r -= c.w) <= 0) || CITIES[0];
      x = city.x + gauss() * city.spread;
      y = city.y + gauss() * city.spread;
    } else {
      // Uniform coverage of the whole territory
      x = Math.random() * 96 + 2;
      y = Math.random() * 96 + 2;
    }
    if (isInsideFrance(x, y)) {
      points.push({
        x,
        y,
        size: Math.random() * 0.6 + 0.35,     // SVG radius (viewBox units)
        delay: Math.random() * 4,             // Pulse phase offset (s)
        duration: Math.random() * 2.5 + 2,    // Pulse duration (s)
        intensity: Math.random() * 0.45 + 0.5 // Peak opacity
      });
    }
  }
  return points;
};

// Generate connections between nearby points to create the mesh network
const getMeshConnections = (points, maxDistance = 6.5) => {
  const lines = [];
  // To avoid huge DOM size, we only link subset of points or limit connections per point
  for (let i = 0; i < points.length; i++) {
    let connections = 0;
    for (let j = i + 1; j < points.length; j++) {
      if (connections >= 3) break; // Limit links to keep structure clean

      const dx = points[i].x - points[j].x;
      const dy = points[i].y - points[j].y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < maxDistance) {
        lines.push({
          id: `${i}-${j}`,
          x1: points[i].x,
          y1: points[i].y,
          x2: points[j].x,
          y2: points[j].y
        });
        connections++;
      }
    }
  }
  return lines;
};

const CommunityVision = () => {
  // Dense constellation: dots rendered as SVG circles with CSS pulse (cheap at this count)
  const points = useMemo(() => generatePoints(1200), []);

  // Create mesh connections for a subset of points to avoid visual clutter
  const meshLines = useMemo(() => getMeshConnections(points.slice(0, 220), 5), [points]);

  return (
    <section
      id="community-vision"
      className="section-padding"
      style={{
        background: 'radial-gradient(circle at 50% 50%, #0c203b 0%, #050b14 100%)',
        position: 'relative',
        overflow: 'hidden',
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
      }}
    >
      <style>{`
        @keyframes cv-pulse {
          0%, 100% { opacity: 0.25; }
          50% { opacity: 1; }
        }
        .cv-dot {
          fill: var(--mga-gold);
          animation: cv-pulse infinite ease-in-out;
        }
      `}</style>

      {/* Decorative Glow */}
      <div
        className="bg-glow-sphere"
        style={{
          width: '50vw',
          height: '50vw',
          background: 'rgba(242, 184, 7, 0.05)',
          top: '15%',
          left: '20%'
        }}
      />

      <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div className="split-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '50px',
          alignItems: 'center'
        }}>

          {/* Left Column - High Tech Constellation France Map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="glass-card split-visual"
            style={{
              padding: '25px',
              background: 'rgba(11, 31, 58, 0.35)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              position: 'relative',
              height: '430px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
            }}
          >
            {/* Map Canvas */}
            <div style={{ position: 'relative', width: '100%', height: '100%', maxWidth: '350px' }}>

              {/* Detailed Map Outline (Subtle background path) */}
              <svg
                viewBox="0 0 100 100"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0.12,
                  stroke: 'var(--mga-white)',
                  strokeWidth: '0.4',
                  fill: 'rgba(255, 255, 255, 0.01)',
                  pointerEvents: 'none',
                  zIndex: 0
                }}
              >
                <polygon points={FRANCE_POLYGON.map(p => p.join(',')).join(' ')} />
              </svg>

              {/* Connected Lines Grid (High Tech Mesh Network) */}
              <svg
                viewBox="0 0 100 100"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 1,
                  pointerEvents: 'none'
                }}
              >
                {meshLines.map(line => (
                  <motion.line
                    key={line.id}
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke="rgba(242, 184, 7, 0.07)"
                    strokeWidth="0.3"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                  />
                ))}
              </svg>

              {/* Mass of Pulsing Glowing Points */}
              <svg
                viewBox="0 0 100 100"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 2,
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 0 2px var(--mga-gold-light))'
                }}
              >
                {points.map((pt, idx) => (
                  <circle
                    key={idx}
                    className="cv-dot"
                    cx={pt.x}
                    cy={pt.y}
                    r={pt.size}
                    style={{
                      opacity: pt.intensity,
                      animationDuration: `${pt.duration}s`,
                      animationDelay: `${pt.delay}s`
                    }}
                  />
                ))}
              </svg>

              {/* Status Header */}
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                backgroundColor: 'rgba(5, 11, 20, 0.85)',
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '0.75rem',
                border: '1px solid rgba(242, 184, 7, 0.25)',
                color: 'var(--mga-gold-light)',
                fontWeight: 600,
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                zIndex: 10
              }}>
                🎯 Notre vision : 10 millions et plus de citoyens connectés
              </div>

              {/* Projection disclaimer */}
              <div style={{
                position: 'absolute',
                bottom: '6px',
                right: '10px',
                fontSize: '0.65rem',
                color: 'rgba(255, 255, 255, 0.45)',
                fontStyle: 'italic',
                zIndex: 10
              }}>
                Simulation — projection de notre objectif
              </div>
            </div>
          </motion.div>

          {/* Right Column - Deterrent Statement */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
          >
            <span className="badge-gold">🕊️ L'Impact Dissuasif</span>
            <h3 style={{
              fontSize: 'clamp(1.8rem, 3.5vw, 2.4rem)',
              color: 'var(--mga-white)',
              lineHeight: 1.25,
              marginBottom: '20px',
              fontFamily: 'var(--font-display)'
            }}>
              Imaginez plus de 10 millions de personnes en France, prêtes à recevoir une alerte. Prêtes à intervenir.
            </h3>

            <p style={{
              fontSize: '1.15rem',
              color: 'var(--mga-slate-300)',
              lineHeight: '1.7',
              marginBottom: '30px'
            }}>
              Si n'importe où dans la rue, à n'importe quelle heure, une personne agressée peut instantanément mobiliser des dizaines d'intervenants coordonnés à proximité, il devient impossible pour un agresseur d'agir en toute impunité. Ce n'est pas encore la réalité : c'est l'objectif que nous construisons — et chaque nouvelle installation nous en rapproche.
            </p>

            <div
              className="glass-card"
              style={{
                padding: '24px 30px',
                background: 'rgba(242, 184, 7, 0.06)',
                borderLeft: '4px solid var(--mga-gold)',
                borderColor: 'var(--mga-gold)',
                boxShadow: '0 8px 32px rgba(242, 184, 7, 0.02)'
              }}
            >
              <p style={{
                margin: 0,
                fontSize: '1.3rem',
                fontWeight: 700,
                color: 'var(--mga-gold-light)',
                fontStyle: 'italic',
                lineHeight: '1.5',
                fontFamily: 'var(--font-display)'
              }}>
                « Au bout de combien de temps pensez-vous que les agresseurs prendront encore le risque de commettre un crime ? »
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default CommunityVision;
